import gql from 'graphql-tag';
import { clients } from '../../util/clients';
import { fieldsToGqlString } from '../../util/shared/vue-global';

const { backend } = clients.direct;

const defaultFields = {
  _id: '',
  fileQueueId: '',
  postingDate: '',
  timestamp: '',
  totalRecords: '',
  totalBatches: '',
  validRecords: '',
  overpaymentRecords: '',
  underpaymentRecords: '',
  notFoundRecords: '',
  duplicateRecords: '',
  totalAmount: '',
  totalOverpaymentAmount: '',
  totalUnderpaymentAmount: '',
  missingFeesRecords: '',
  batches: {
    batchNumber: '',
    totalRecords: '',
    validRecords: '',
    overpaymentRecords: '',
    underpaymentRecords: '',
    notFoundRecords: '',
    duplicateRecords: '',
    totalAmount: '',
    totalTaxPayment: '',
    totalPenaltyPayment: '',
    totalFeePayment: '',
    totalOverpaymentAmount: '',
    totalUnderpaymentAmount: '',
    missingFeesRecords: '',
    paymentApplied: '',
    paymentId: '',
    paymentNumber: '',
    fiscalYear: '',
  },
  records: {
    batchNumber: '',
    recordType: '',
    taxYear: '',
    taxId: '',
    propertyOrParcelId: '',
    loanNumber: '',
    taxPayment: '',
    penaltyPayment: '',
    feePayment: '',
    overpaymentAmount: '',
    totalPayment: '',
    paidDate: '',
    expectedAmount: '',
    expectedTax: '',
    expectedPenalty: '',
    expectedFee: '',
    expectedPenaltyPrevMonth: '',
    expectedAmountPrevMonth: '',
    calculatedPenalty: '',
    amountDifference: '',
    validationStatus: '',
    errorDescription: '',
    // Adjustment tracking
    isAdjusted: '',
    adjustedBy: '',
    adjustedAt: '',
    originalTaxPayment: '',
    originalPenaltyPayment: '',
    originalFeePayment: '',
    originalOverpaymentAmount: '',
    // Original tax reference (when rematched)
    originalTaxYear: '',
    originalTaxId: '',
    isRematched: '',
    rematchedBy: '',
    rematchedAt: '',
    // Approval tracking
    isApproved: '',
    approvedBy: '',
    approvedAt: '',
    // Over/under threshold flag
    hasOverUnderAdjustment: '',
    // Previous month penalty flag
    usedPrevMonthPenalty: '',
    // Missing-fees flag (bank file shows $0 but system has expectedFee > 0)
    isMissingFees: '',
  },
};

export const LockboxImportActions = {
  async loadLockboxImport({ commit, dispatch }, { criteria, fields }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });
      const gqlfields = fields || defaultFields;

      results = await backend.query({
        query: gql`query lockboxImports($criteria: lockboxImportCriteriaType) {
          lockboxImports(criteria: $criteria) {
            ${fieldsToGqlString(gqlfields)}
          }
        }`,
        variables: { criteria },
        fetchPolicy: 'no-cache',
      });

      clients.handleGQLErrors(results, commit);
      const { lockboxImports } = results.data;
      return lockboxImports || [];
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem loading lockbox imports.', { root: true });
      return [];
    }
  },

  async createPaymentFromLockbox({ commit, dispatch }, {
    fileQueueId,
    batchNumber,
    workstation,
    applyOverPayments,
    skipPartials,
    skipDuplicates,
    paymentType,
    checkNumber,
    eftType,
    creditType,
    paidBy,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation createPaymentFromLockboxImport(
          $fileQueueId: ID!
          $batchNumber: String!
          $workstation: Int!
          $applyOverPayments: Boolean
          $skipPartials: Boolean
          $skipDuplicates: Boolean
          $paymentType: String
          $checkNumber: String
          $eftType: String
          $creditType: String
          $paidBy: String
        ) {
          createPaymentFromLockboxImport(
            fileQueueId: $fileQueueId
            batchNumber: $batchNumber
            workstation: $workstation
            applyOverPayments: $applyOverPayments
            skipPartials: $skipPartials
            skipDuplicates: $skipDuplicates
            paymentType: $paymentType
            checkNumber: $checkNumber
            eftType: $eftType
            creditType: $creditType
            paidBy: $paidBy
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          batchNumber,
          workstation,
          applyOverPayments,
          skipPartials,
          skipDuplicates,
          paymentType,
          checkNumber,
          eftType,
          creditType,
          paidBy,
        },
      });

      clients.handleGQLErrors(results, commit);
      const { createPaymentFromLockboxImport } = results.data;
      return createPaymentFromLockboxImport;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem creating the payment.', { root: true });
      throw e;
    }
  },

  async createPaymentFromLockboxAllBatches({ commit, dispatch }, {
    fileQueueId,
    workstation,
    applyOverPayments,
    skipPartials,
    skipDuplicates,
    paymentType,
    checkNumber,
    eftType,
    creditType,
    paidBy,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation createPaymentFromLockboxImportAllBatches(
          $fileQueueId: ID!
          $workstation: Int!
          $applyOverPayments: Boolean
          $skipPartials: Boolean
          $skipDuplicates: Boolean
          $paymentType: String
          $checkNumber: String
          $eftType: String
          $creditType: String
          $paidBy: String
        ) {
          createPaymentFromLockboxImportAllBatches(
            fileQueueId: $fileQueueId
            workstation: $workstation
            applyOverPayments: $applyOverPayments
            skipPartials: $skipPartials
            skipDuplicates: $skipDuplicates
            paymentType: $paymentType
            checkNumber: $checkNumber
            eftType: $eftType
            creditType: $creditType
            paidBy: $paidBy
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          workstation,
          applyOverPayments,
          skipPartials,
          skipDuplicates,
          paymentType,
          checkNumber,
          eftType,
          creditType,
          paidBy,
        },
      });

      clients.handleGQLErrors(results, commit);
      const { createPaymentFromLockboxImportAllBatches } = results.data;
      return createPaymentFromLockboxImportAllBatches;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem creating payments for all batches.', { root: true });
      throw e;
    }
  },

  async getFileQueueStatus({ commit, dispatch }, { fileQueueId }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.query({
        query: gql`query getFileQueueStatus($fileQueueId: ID!) {
          fileQueue(_id: $fileQueueId) {
            _id
            Status
            Parameters
            ErrorDetail {
              LineNumber
              ErrorDescription
            }
          }
        }`,
        variables: { fileQueueId },
        fetchPolicy: 'network-only',
      });

      clients.handleGQLErrors(results, commit);
      const { fileQueue } = results.data;
      return fileQueue;
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      throw e;
    }
  },

  async updateRecordValidationStatus({ commit, dispatch }, {
    fileQueueId, recordIndex, validationStatus,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation updateLockboxRecordValidationStatus(
          $fileQueueId: ID!
          $recordIndex: Int!
          $validationStatus: String!
        ) {
          updateLockboxRecordValidationStatus(
            fileQueueId: $fileQueueId
            recordIndex: $recordIndex
            validationStatus: $validationStatus
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          recordIndex,
          validationStatus,
        },
      });

      clients.handleGQLErrors(results, commit);
      if (results && results.data && results.data.updateLockboxRecordValidationStatus) {
        return results.data.updateLockboxRecordValidationStatus;
      }
      return { success: false, message: 'No response from server' };
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem updating the record status.', { root: true });
      throw e;
    }
  },

  async updateRecordPayments({ commit, dispatch }, {
    fileQueueId, recordIndex, taxPayment, penaltyPayment, feePayment, overpaymentAmount,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation updateLockboxRecordPayments(
          $fileQueueId: ID!
          $recordIndex: Int!
          $taxPayment: Float!
          $penaltyPayment: Float!
          $feePayment: Float!
          $overpaymentAmount: Float
        ) {
          updateLockboxRecordPayments(
            fileQueueId: $fileQueueId
            recordIndex: $recordIndex
            taxPayment: $taxPayment
            penaltyPayment: $penaltyPayment
            feePayment: $feePayment
            overpaymentAmount: $overpaymentAmount
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          recordIndex,
          taxPayment,
          penaltyPayment,
          feePayment,
          overpaymentAmount,
        },
      });

      clients.handleGQLErrors(results, commit);
      if (results && results.data && results.data.updateLockboxRecordPayments) {
        return results.data.updateLockboxRecordPayments;
      }
      return { success: false, message: 'No response from server' };
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem updating the payment amounts.', { root: true });
      throw e;
    }
  },

  async approveRecord({ commit, dispatch }, {
    fileQueueId, recordIndex, isApproved,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation approveLockboxRecord(
          $fileQueueId: ID!
          $recordIndex: Int!
          $isApproved: Boolean!
        ) {
          approveLockboxRecord(
            fileQueueId: $fileQueueId
            recordIndex: $recordIndex
            isApproved: $isApproved
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          recordIndex,
          isApproved,
        },
      });

      clients.handleGQLErrors(results, commit);
      if (results && results.data && results.data.approveLockboxRecord) {
        return results.data.approveLockboxRecord;
      }
      return { success: false, message: 'No response from server' };
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem updating the approval status.', { root: true });
      throw e;
    }
  },

  async rematchRecord({ commit, dispatch }, {
    fileQueueId, recordIndex, taxYear, taxId,
  }) {
    let results;
    try {
      await dispatch('reAuth', null, { root: true });

      results = await backend.mutate({
        mutation: gql`mutation rematchLockboxRecord(
          $fileQueueId: ID!
          $recordIndex: Int!
          $taxYear: Int!
          $taxId: Int!
        ) {
          rematchLockboxRecord(
            fileQueueId: $fileQueueId
            recordIndex: $recordIndex
            taxYear: $taxYear
            taxId: $taxId
          ) {
            success
            message
          }
        }`,
        variables: {
          fileQueueId,
          recordIndex,
          taxYear,
          taxId,
        },
      });

      clients.handleGQLErrors(results, commit);
      if (results && results.data && results.data.rematchLockboxRecord) {
        return results.data.rematchLockboxRecord;
      }
      return { success: false, message: 'No response from server' };
    } catch (e) {
      dispatch('logException', e, { root: true });
      clients.handleGQLErrors(results, commit);
      dispatch('flashError', 'There was a problem rematching the record.', { root: true });
      throw e;
    }
  },
};

export default LockboxImportActions;
